import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import leapifyLogo from 'assets/images/Leapify-Logo.svg';
import { NotFoundIcon } from 'ui/components/icon';
import { PublicWrapper } from 'pages/public/containers';
import { useQueryParams } from 'hooks';
import { useResendOtp, useVerifyOtpV3 } from 'pages/private/hooks';
import { Loading } from 'ui/partials/PagePlaceholder/PagePlaceholder';
import { Button } from 'ui/components/common';
import { useVerifyOtp } from 'pages/public/hooks';
import { FaRegCheckCircle } from 'react-icons/fa';
import { cn } from 'utils/helpers';

const VerficationStatus: FC<{
  title: string;
  description: string | ReactNode;
  button?: ReactNode;
  icon?: ReactNode;
}> = ({ title, description, button, icon }) => {
  return (
    <div
      className={cn(
        'w-full max-w-[600px] p-8 rounded-[6px]',
        'bg-fill-tertiary shadow-custom text-center',
      )}
    >
      <img
        alt="Leapify Logo"
        className="w-[137px] mb-16 inline-block mx-auto"
        src={leapifyLogo}
      />
      <div className="w-[75px] m-auto">{icon}</div>
      <div className="pb-8 mt-7">
        <h3 className="text-secondary text-[22px] font-semibold mb-3">
          {title}
        </h3>
        <div>
          <p className="text-[14px] font-normal">{description}</p>
        </div>
      </div>
      {button}
    </div>
  );
};

const VerifyLink: FC = () => {
  const [{ id, otp }] = useQueryParams();
  const { mutate: verifyOtp, isLoading: isVerifyOtpLoading } = useVerifyOtp();
  const { mutate: verifyOtpV3, isLoading: isVerifyOtpV3Loading } =
    useVerifyOtpV3();
  const { mutate: resendOtp, isLoading: isResendOtpLoading } = useResendOtp();
  const [isOtpValid, setIsOtpValid] = useState<
    'valid' | 'invalid' | 'expired' | null
  >('expired');
  const [isValidated, setIsValidated] = useState(false);

  useEffect(() => {
    const handleUnload = () => {
      if (!isValidated && isOtpValid === 'valid')
        navigator.sendBeacon(
          `${import.meta.env.VITE_APP_END_POINT}/api/v3/auth/accounts/otp/verify`,
          new URLSearchParams({ id: String(id), otp: String(otp) }),
        );
    };

    window.addEventListener('unload', handleUnload);

    return () => window.removeEventListener('unload', handleUnload);
  }, [id, isValidated, isOtpValid, otp]);

  useEffect(() => {
    if (id && otp)
      verifyOtp(
        {
          referenceId: id as string,
          otp: otp as string,
          type: 'REGISTRATION',
          source: 'account',
        },
        {
          onSuccess: () => {
            setIsOtpValid('valid');
          },
          onError: (err) => {
            const errorMessage = (err as unknown as Record<string, any>)?.data
              ?.message;

            if (errorMessage === 'OTP expired.') {
              setIsOtpValid('expired');
              return;
            }

            setIsOtpValid('invalid');
          },
        },
      );
  }, [id, otp, verifyOtp]);

  const isValid = useMemo(() => Boolean(id && otp), [id, otp]);

  const handleResendVerification = useCallback(() => {
    if (id)
      resendOtp(id, {
        onSuccess: () => {
          setIsValidated(true);
          setTimeout(() => window.close(), 3000);
        },
      });
  }, [id, resendOtp]);

  const handleVerifyOtpV3 = useCallback(() => {
    verifyOtpV3(
      { id, otp },
      {
        onSuccess: () => {
          setIsValidated(true);
          setTimeout(() => window.close(), 3000);
        },
      },
    );
  }, [id, otp, verifyOtpV3]);

  useEffect(() => {
    if (isOtpValid === 'valid')
      setTimeout(
        () =>
          verifyOtpV3(
            { id, otp },
            {
              onSuccess: () => window.close(),
            },
          ),
        10000,
      );
  }, [id, isOtpValid, otp, verifyOtpV3]);

  const title = useMemo(() => {
    if (!isValid || isOtpValid === 'invalid')
      return 'Invalid Verification Link';
    if (isOtpValid === 'expired') return 'Verification Link Expired';
    if (isOtpValid === 'valid') return 'Email Verified Successfully';

    return '';
  }, [isOtpValid, isValid]);

  const description = useMemo(() => {
    if (!isValid || isOtpValid === 'invalid')
      return (
        <>
          <p>
            The verification link you used is invalid or may have already been
            used.
          </p>
          <p>Please request a new verification email to continue.</p>
        </>
      );
    if (isOtpValid === 'expired')
      return (
        <>
          <p>This verification link has expired for security reasons.</p>
          <p>Please request a new verification email to continue.</p>
        </>
      );
    if (isOtpValid === 'valid')
      return (
        <>
          <p>Your email address has been verified.</p>
          <p>You can now return to the application and continue.</p>
        </>
      );

    return '';
  }, [isOtpValid, isValid]);

  const ctaButton = useMemo(() => {
    if (!isValid || isOtpValid === 'invalid')
      return {
        action: () => (window.location.href = '/'),
        label: 'BACK TO LOGIN',
        disabled: false,
      };
    if (isOtpValid === 'expired')
      return {
        action: handleResendVerification,
        label: 'RESEND VERIFICATION LINK',
        disabled: isValidated || isResendOtpLoading,
      };
    if (isOtpValid === 'valid')
      return {
        action: handleVerifyOtpV3,
        label: 'OKAY',
        disabled: isValidated || isVerifyOtpV3Loading,
      };

    return {
      action: () => {},
      label: '',
      disabled: true,
    };
  }, [
    handleResendVerification,
    handleVerifyOtpV3,
    isOtpValid,
    isResendOtpLoading,
    isValid,
    isValidated,
    isVerifyOtpV3Loading,
  ]);

  return (
    <PublicWrapper
      className={cn(
        'flex items-center md:justify-center flex-col',
        'px-6 py-[22px] md:py-0',
      )}
    >
      {!isValid ? (
        <VerficationStatus
          title={title}
          button={
            <Button
              onClick={() => (window.location.href = '/')}
              size="sm"
              label="BACK TO LOGIN"
              intent="primary"
              className="mx-auto"
              disabled={isVerifyOtpLoading}
            />
          }
          icon={<NotFoundIcon className="text-content" />}
          description={description}
        />
      ) : isVerifyOtpLoading || isVerifyOtpV3Loading ? (
        <Loading title="" description="" />
      ) : (
        <VerficationStatus
          title={title}
          button={
            <Button
              onClick={ctaButton.action}
              size="sm"
              label={ctaButton.label}
              intent="primary"
              className="mx-auto"
              disabled={ctaButton.disabled}
            />
          }
          icon={
            isOtpValid === 'valid' ? (
              <FaRegCheckCircle className="text-[75px] text-primary-base" />
            ) : (
              <NotFoundIcon className="text-content" />
            )
          }
          description={description}
        />
      )}
    </PublicWrapper>
  );
};

export default VerifyLink;
